import React from 'react';
import PropTypes from 'prop-types';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import { StatCard, Panel, STAT_ICONS } from '../../../../component/common/designSystem';
import {
  useProgramme,
  TrendPanel,
  WeekdayPanel,
  SplitPanel,
  CareFootnote,
  orDash
} from './CareShell';
import { programmeColor, TENURE_RAMP, ACTIVITY_RAMP } from './careTheme';

/**
 * One programme's page. All six routes render this component — the six
 * programmes differ in what they ask people to record, and this section reads
 * none of that, so there is genuinely nothing left for six separate pages to
 * say differently.
 *
 * The page answers two questions and no others:
 *
 *   IS IT REACHING ANYONE?   enrolled, take-up, joined this month, tenure
 *   DO THEY COME BACK?       active, dormant, days logged, retention, trend
 *
 * THE DORMANT FIGURE IS NOT A REPROACH. Somebody enrolled in Fibromyalgia Care
 * who has not logged for a month may be having a good month. It is here because
 * a benefits team deciding whether to keep paying for something needs to know
 * the difference between a programme people use and a programme people signed
 * up to once — not so anybody gets chased.
 */
const ProgrammePage = ({ fetcher, populationLabel = 'users' }) => {
  const [stats, gate] = useProgramme(fetcher);
  if (gate) return gate;

  const colour = programmeColor(stats.key);
  const unit = stats.unit || 'check-ins';

  return (
    <div>
      {/* ---------- Reach ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="Enrolled"
          value={formatBigNumber(stats.participants)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={`${stats.adoption}%`}
        />
        <StatCard
          icon={STAT_ICONS.newUsers}
          title="Joined (30d)"
          value={formatBigNumber(stats.joinedInWindow)}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Active (30d)"
          value={formatBigNumber(stats.activeUsers)}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title={`Check-ins (30d)`}
          value={formatBigNumber(stats.checkIns)}
        />
        {/* Null rather than 0% when nobody was checking in a fortnight ago —
            there was nobody to retain, which is not the same as losing them. */}
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Week-on-week hold"
          value={orDash(stats.retentionRate, '%')}
        />
      </div>

      {/* ---------- The honest pair ---------- */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Still using it" subtitle="Of everybody enrolled, who checked in this month">
          <div className="py-4 text-center">
            <p className="text-4xl font-semibold text-shoorah-btn dark:text-white">
              {stats.activeRate}%
            </p>
            <p className="mt-2 text-sm text-shoorah-secondary dark:text-gray-400">
              {formatBigNumber(stats.activeUsers)} of {formatBigNumber(stats.participants)} enrolled
            </p>
          </div>
        </Panel>

        <Panel title="Enrolled but quiet" subtitle="No check-in in the last 30 days">
          <div className="py-4 text-center">
            <p className="text-4xl font-semibold text-shoorah-btn dark:text-white">
              {formatBigNumber(stats.dormant)}
            </p>
            <p className="mt-2 text-sm text-shoorah-secondary dark:text-gray-400">
              Not a problem on its own — a quiet month can be a good month.
            </p>
          </div>
        </Panel>

        <Panel title="Days logged" subtitle="Average distinct days per active person">
          <div className="py-4 text-center">
            <p className="text-4xl font-semibold text-shoorah-btn dark:text-white">
              {stats.avgActiveDays}
            </p>
            <p className="mt-2 text-sm text-shoorah-secondary dark:text-gray-400">
              out of the last 30, counting only people who logged at all
            </p>
          </div>
        </Panel>
      </div>

      {/* ---------- Trend ---------- */}
      <div className="mt-4">
        <TrendPanel trend={stats.trend} color={colour} unit={unit} />
      </div>

      {/* ---------- Distributions ---------- */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SplitPanel
          title="How long people have been enrolled"
          subtitle="Time since joining, whatever their check-in habit"
          rows={stats.tenureSplit}
          ramp={TENURE_RAMP}
          unit="people"
          empty="Nobody is enrolled in the current view."
        />
        <SplitPanel
          title="How often they check in"
          subtitle="Distinct days in the last 30, among people who logged at all"
          rows={stats.activeDaySplit}
          ramp={ACTIVITY_RAMP}
          unit="people"
          empty="No check-ins in the last 30 days to spread out."
        />
      </div>

      {/* ---------- Weekday ---------- */}
      <div className="mt-4">
        <WeekdayPanel rows={stats.weekdaySplit} unit={unit} />
      </div>

      {/* The base is shown so the percentage above can be judged. "50% held" off
          two people is not the same claim as 50% off two hundred. */}
      {stats.retentionBase > 0 ? (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Week-on-week hold compares the people who checked in during the week before last with
          those who checked in this week — {formatBigNumber(stats.retentionBase)}{' '}
          {stats.retentionBase === 1 ? 'person was' : 'people were'} in the earlier week.
        </p>
      ) : (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Week-on-week hold is not shown: nobody checked in during the week before last, so there
          was nobody to hold on to.
        </p>
      )}

      <CareFootnote minimum={stats.minimum} />
    </div>
  );
};

ProgrammePage.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default ProgrammePage;
